import { Link } from 'react-router-dom';
import { dateTime, humanise, inr, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Chips, Notice, StatusBadge } from '../components/ui';

interface BonusRow {
  id: string;
  type: string;
  amountPaise: number;
  payoutStatus: string;
  payoutRef: string | null;
  attemptCount: number;
  failureReason: string | null;
  triggerType: string;
  paidAt: string | null;
  createdAt: string;
  beneficiary: { id: string; referralCode: string; user: { id: string; mobile: string; displayName: string | null } };
}

/**
 * FR-AGT-08's audit ledger.
 *
 * BLOCKED is styled amber rather than red on purpose: it is a deliberate hold,
 * not a failure, and the difference matters to whoever reads this screen.
 */
export function BonusesPage() {
  const { get, set } = useListParams();
  const payoutStatus = get('payoutStatus');

  const columns: Array<Column<BonusRow>> = [
    {
      key: 'beneficiary',
      header: 'Partner',
      render: (r) => (
        <div>
          <Link to={`/users/${r.beneficiary.user.id}`}>
            {r.beneficiary.user.displayName ?? fmtMobile(r.beneficiary.user.mobile)}
          </Link>
          <div className="cell-mono">{r.beneficiary.referralCode}</div>
        </div>
      ),
    },
    { key: 'type', header: 'Bonus', render: (r) => humanise(r.type) },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      render: (r) => <span className="cell-strong">{inr(r.amountPaise)}</span>,
    },
    { key: 'trigger', header: 'Triggered by', render: (r) => <span className="cell-muted">{humanise(r.triggerType)}</span> },
    {
      key: 'status',
      header: 'Payout',
      render: (r) => (
        <div>
          <StatusBadge value={r.payoutStatus} />
          {r.failureReason && (
            <div className="cell-muted cell-clip" title={r.failureReason} style={{ maxWidth: 260 }}>
              {r.failureReason}
            </div>
          )}
        </div>
      ),
    },
    { key: 'attempts', header: 'Attempts', numeric: true, render: (r) => r.attemptCount },
    { key: 'accrued', header: 'Accrued', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
    { key: 'paid', header: 'Paid', render: (r) => <span className="cell-muted">{dateTime(r.paidAt)}</span> },
  ];

  return (
    <div className="stack">
      <Notice tone="warn" title="Payouts are disabled">
        Bonuses accrue here in full and are auditable, but nothing is disbursed while <code>PAYOUTS_ENABLED</code> is
        false. The structure is under legal review (Prize Chits and Money Circulation Schemes (Banning) Act 1978 — PRS
        v1 §7.5, v2 §0.2a). Nothing is lost by waiting; the backlog pays out when it is cleared.
      </Notice>

      <DataTable<BonusRow>
        queryKey={['bonuses', payoutStatus]}
        path="/admin/bonuses"
        params={{ payoutStatus }}
        columns={columns}
        emptyIcon="⚖"
        emptyTitle="No bonuses recorded"
        footer={(page) => {
          const byStatus = page.meta.byStatus as Array<{ status: string; count: number; totalPaise: number }> | undefined;
          if (!byStatus?.length) return null;
          return (
            <div
              className="row row--wrap"
              style={{ padding: '12px var(--s5)', borderTop: '1px solid var(--border)', gap: 'var(--s5)' }}
            >
              {byStatus.map((s) => (
                <span key={s.status} className="row" style={{ gap: 7 }}>
                  <StatusBadge value={s.status} />
                  <strong>{inr(s.totalPaise)}</strong>
                  <span className="cell-muted">({s.count})</span>
                </span>
              ))}
            </div>
          );
        }}
        toolbar={
          <Chips
            value={payoutStatus}
            onChange={(v) => set('payoutStatus', v)}
            options={[
              { value: '', label: 'All' },
              { value: 'BLOCKED', label: 'On hold' },
              { value: 'PENDING', label: 'Pending' },
              { value: 'PROCESSING', label: 'Processing' },
              { value: 'PAID', label: 'Paid' },
              { value: 'FAILED', label: 'Failed' },
            ]}
          />
        }
      />
    </div>
  );
}
